import { startOfMonth, endOfMonth, eachDayOfInterval, format } from 'date-fns';
import type { Schedule } from '@vehix/portal/services/api/scheduling';
import { type CronParts, parseCron, cronRunsOnDay } from './cronFields';

// Re-exported so existing importers of this module keep working, and so there
// is one obvious place to find cron field parsing from either surface.
export {
  type CronParts,
  parseCron,
  fieldMatches,
  cronRunsOnDay,
  matchingValues
} from './cronFields';

/**
 * Represents a scheduled occurrence on a specific day
 */
export interface ScheduledDay {
  date: Date;
  schedules: ScheduleOccurrence[];
}

export interface ScheduleOccurrence {
  schedule: Schedule;
  time: string; // "6:00 AM" format
  hour: number;
  minute: number;
}

/**
 * Get the scheduled time from a cron expression
 * Returns the hour and minute when the job runs
 */
function getScheduledTime(cron: CronParts): { hour: number; minute: number; timeString: string } {
  let hour = 0;
  let minute = 0;

  // Parse hour - if it's a step like */6, use the first occurrence (0)
  if (cron.hour !== '*') {
    if (cron.hour.includes('/')) {
      hour = 0;
    } else if (cron.hour.includes(',')) {
      hour = parseInt(cron.hour.split(',')[0], 10);
    } else if (cron.hour.includes('-')) {
      hour = parseInt(cron.hour.split('-')[0], 10);
    } else {
      hour = parseInt(cron.hour, 10);
    }
  }

  // Parse minute
  if (cron.minute !== '*') {
    if (cron.minute.includes('/')) {
      minute = 0;
    } else if (cron.minute.includes(',')) {
      minute = parseInt(cron.minute.split(',')[0], 10);
    } else if (cron.minute.includes('-')) {
      minute = parseInt(cron.minute.split('-')[0], 10);
    } else {
      minute = parseInt(cron.minute, 10);
    }
  }

  // Format time string
  const period = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  const timeString = `${displayHour}:${minute.toString().padStart(2, '0')} ${period}`;

  return { hour, minute, timeString };
}

/**
 * Get all days in a month where a cron expression will run
 */
export function getScheduleDaysForMonth(cronExpression: string, month: Date): Date[] {
  const cron = parseCron(cronExpression);
  if (!cron) return [];

  const start = startOfMonth(month);
  const end = endOfMonth(month);
  const allDays = eachDayOfInterval({ start, end });

  return allDays.filter(day => cronRunsOnDay(cron, day));
}

/**
 * Group schedules by the day they run within a given month
 * Returns a Map where keys are date strings (YYYY-MM-DD) and values are schedule occurrences
 */
export function groupSchedulesByDay(
  schedules: Schedule[],
  month: Date
): Map<string, ScheduleOccurrence[]> {
  const result = new Map<string, ScheduleOccurrence[]>();

  // Only include enabled schedules
  const enabledSchedules = schedules.filter(s => s.is_enabled);

  for (const schedule of enabledSchedules) {
    const cron = parseCron(schedule.cron_expression);
    if (!cron) continue;

    const runDays = getScheduleDaysForMonth(schedule.cron_expression, month);
    const { hour, minute, timeString } = getScheduledTime(cron);

    for (const day of runDays) {
      const dateKey = format(day, 'yyyy-MM-dd');
      const occurrence: ScheduleOccurrence = {
        schedule,
        time: timeString,
        hour,
        minute
      };

      const existing = result.get(dateKey) || [];
      existing.push(occurrence);
      // Sort by time
      existing.sort((a, b) => {
        if (a.hour !== b.hour) return a.hour - b.hour;
        return a.minute - b.minute;
      });
      result.set(dateKey, existing);
    }
  }

  return result;
}

/**
 * Get scheduled days for a date range (used for week view)
 */
export function getScheduledDaysInRange(
  schedules: Schedule[],
  startDate: Date,
  endDate: Date
): ScheduledDay[] {
  const result: ScheduledDay[] = [];
  const days = eachDayOfInterval({ start: startDate, end: endDate });

  // Only include enabled schedules
  const enabledSchedules = schedules.filter(s => s.is_enabled);

  for (const day of days) {
    const occurrences: ScheduleOccurrence[] = [];

    for (const schedule of enabledSchedules) {
      const cron = parseCron(schedule.cron_expression);
      if (!cron) continue;

      if (cronRunsOnDay(cron, day)) {
        const { hour, minute, timeString } = getScheduledTime(cron);
        occurrences.push({
          schedule,
          time: timeString,
          hour,
          minute
        });
      }
    }

    if (occurrences.length > 0) {
      // Sort by time
      occurrences.sort((a, b) => {
        if (a.hour !== b.hour) return a.hour - b.hour;
        return a.minute - b.minute;
      });
      result.push({ date: day, schedules: occurrences });
    }
  }

  return result;
}

/**
 * Get the number of schedules that run on a specific date
 */
export function getScheduleCountForDate(schedules: Schedule[], date: Date): number {
  return schedules.filter(schedule => {
    if (!schedule.is_enabled) return false;
    const cron = parseCron(schedule.cron_expression);
    if (!cron) return false;
    return cronRunsOnDay(cron, date);
  }).length;
}

/**
 * Determine the frequency type from a cron expression
 * Used for visual indicators on the calendar
 */
export function getCronFrequency(
  cronExpression: string
): 'daily' | 'weekly' | 'monthly' | 'custom' {
  const cron = parseCron(cronExpression);
  if (!cron) return 'custom';

  const { dayOfMonth, dayOfWeek } = cron;

  // Monthly: specific day of month, any day of week
  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    return 'monthly';
  }

  // Weekly: any day of month, specific day(s) of week
  if (dayOfMonth === '*' && dayOfWeek !== '*') {
    return 'weekly';
  }

  // Daily: wildcards for both day fields
  if (dayOfMonth === '*' && dayOfWeek === '*') {
    return 'daily';
  }

  return 'custom';
}

/**
 * Get the scheduled time (hour and minute) from a cron expression
 * Useful for sorting schedules by their run time
 */
export function getScheduledTimeFromCron(cronExpression: string): {
  hour: number;
  minute: number;
  timeString: string;
} {
  const cron = parseCron(cronExpression);
  if (!cron) {
    return { hour: 0, minute: 0, timeString: '12:00 AM' };
  }
  return getScheduledTime(cron);
}

/**
 * Build a 5-field cron string from picker-friendly inputs.
 * dayOfMonth and month are always wildcarded — the picker only models
 * "time of day + days of week".
 */
export function buildCronFromPicker(hour: number, minute: number, daysOfWeek: number[]): string {
  const dow = daysOfWeek.length === 7 ? '*' : [...daysOfWeek].sort((a, b) => a - b).join(',');
  return `${minute} ${hour} * * ${dow}`;
}

/**
 * Inverse of buildCronFromPicker. Returns null when the cron uses patterns the
 * picker can't represent (steps, day-of-month, non-wildcard month, ranges other
 * than the simple full-week comma list / "*"), so the dialog can fall back to
 * a custom-mode banner.
 */
export function parseCronForPicker(cronExpression: string): {
  hour: number;
  minute: number;
  daysOfWeek: number[];
} | null {
  const cron = parseCron(cronExpression);
  if (!cron) return null;

  if (cron.dayOfMonth !== '*' || cron.month !== '*') return null;

  const minute = parseSingleInt(cron.minute, 0, 59);
  if (minute === null) return null;

  const hour = parseSingleInt(cron.hour, 0, 23);
  if (hour === null) return null;

  const daysOfWeek = parseDayOfWeek(cron.dayOfWeek);
  if (!daysOfWeek) return null;

  return { hour, minute, daysOfWeek };
}

function parseSingleInt(field: string, min: number, max: number): number | null {
  if (field === '*' || field.includes('/') || field.includes(',') || field.includes('-')) {
    return null;
  }
  const n = parseInt(field, 10);
  if (isNaN(n) || n < min || n > max) return null;
  return n;
}

function expandDayOfWeekPart(part: string, out: Set<number>): boolean {
  if (part.includes('-')) {
    const segs = part.split('-');
    const start = parseInt(segs[0] ?? '', 10);
    const end = parseInt(segs[1] ?? '', 10);
    if (isNaN(start) || isNaN(end) || start < 0 || end > 6 || start > end) return false;
    for (let i = start; i <= end; i++) out.add(i);
    return true;
  }
  const n = parseInt(part, 10);
  if (isNaN(n) || n < 0 || n > 6) return false;
  out.add(n);
  return true;
}

function parseDayOfWeek(field: string): number[] | null {
  if (field.includes('/')) return null;
  if (field === '*') return [0, 1, 2, 3, 4, 5, 6];

  const out = new Set<number>();
  for (const part of field.split(',')) {
    if (!expandDayOfWeekPart(part, out)) return null;
  }
  return out.size === 0 ? null : [...out].sort((a, b) => a - b);
}

const TZ_LABEL: Record<string, string> = {
  'America/New_York': 'ET',
  'America/Chicago': 'CT',
  'America/Denver': 'MT',
  'America/Los_Angeles': 'PT',
  UTC: 'UTC'
};

const DAY_SHORT: Record<string, string> = {
  '0': 'Sun',
  '1': 'Mon',
  '2': 'Tue',
  '3': 'Wed',
  '4': 'Thu',
  '5': 'Fri',
  '6': 'Sat'
};

/**
 * Convert a 5-field cron expression into a human-readable string.
 * Handles the patterns the picker emits (single hour/minute, comma-list dayOfWeek,
 * "*" or "1-5"); falls back to the raw expression for anything else.
 */
export function humanizeCron(cron: string, timeZone: string): string {
  const tzLabel = TZ_LABEL[timeZone] || timeZone;

  const parts = cron.split(' ');
  if (parts.length !== 5) return `${cron} (${tzLabel})`;

  const [min = '0', hour = '0', , , dow = '*'] = parts;

  const formatHour = (h: string): string => {
    const n = parseInt(h, 10);
    if (isNaN(n)) return h;
    const ampm = n >= 12 ? 'PM' : 'AM';
    const h12 = n % 12 || 12;
    return `${h12}:${min.padStart(2, '0')} ${ampm}`;
  };

  const formatDow = (d: string): string => {
    if (d === '*') return 'Every day';
    if (d === '1-5') return 'Weekdays';
    if (d === '0,6' || d === '6,0') return 'Weekends';
    return d
      .split(',')
      .map(v => DAY_SHORT[v] || v)
      .join(', ');
  };

  return `${formatDow(dow)} at ${formatHour(hour)} ${tzLabel}`;
}
